/* eslint-disable no-unused-vars */
import { useState } from 'react';
import { $, fd, td } from '../theme';
import Ic from '../Icon';
import { sendLateNotice } from '../useEmail';

export default function LateFees({ pays = [], tenants = [], leases = [], lateFees = [], addLateFee }) {
  const [graceDays, setGraceDays] = useState(5);
  const [feeType,   setFeeType]   = useState('flat');
  const [feeAmt,    setFeeAmt]    = useState(75);
  const [feePct,    setFeePct]    = useState(5);
  const [saving,    setSaving]    = useState(null);

  const today = new Date(td());

  // IDs already charged this session or persisted
  const chargedIds = new Set(lateFees.map(f => f.tenant_id).filter(Boolean));

  const lateTenants = tenants.map(t => {
    const lease  = leases.find(l => (l.tenant_id || l.tid) === t.id && (l.status || l.st) === 'active');
    const rent   = lease ? (lease.monthly_rent || lease.rent || 0) : (t.monthly_rent || t.rent || 0);
    const isLate = (t.balance_due || t.bal || 0) > 0 || (t.status || t.st) === 'late';
    if (!isLate) return null;

    const dueDate  = new Date(today.getFullYear(), today.getMonth(), 1 + graceDays);
    const daysLate = Math.max(0, Math.ceil((today - dueDate) / 86400000));
    const fee      = feeType === 'flat' ? feeAmt : Math.round((rent * feePct) / 100);

    return { tenant: t, rent, daysLate, fee, balance: t.balance_due || t.bal || 0 };
  }).filter(Boolean);

  const totalFees    = lateTenants.reduce((s, x) => s + x.fee, 0);
  const totalBalance = lateTenants.reduce((s, x) => s + x.balance, 0);

  const chargeFee = async (t, fee) => {
    if (!addLateFee) return;
    setSaving(t.id);
    await addLateFee({
      tenant_id:   t.id,
      tenant_name: t.full_name || t.name,
      amount:      fee,
      fee_type:    feeType,
    });
    if (t.email) sendLateNotice({ tenantEmail: t.email, tenantName: t.full_name || t.name, balanceDue: t.balance_due || t.bal || 0 });
    setSaving(null);
  };

  const chargeAll = async () => {
    for (const { tenant: t, fee } of lateTenants) {
      if (!chargedIds.has(t.id)) await chargeFee(t, fee);
    }
  };

  return (
    <div>
      <div className="card">
        <div className="card-hd"><h3>Late Fee Settings</h3></div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
          <div className="fi">
            <label>Grace Period (days)</label>
            <input type="number" min={0} max={30} value={graceDays} onChange={e => setGraceDays(parseInt(e.target.value)||0)}/>
          </div>
          <div className="fi">
            <label>Fee Type</label>
            <select value={feeType} onChange={e => setFeeType(e.target.value)}>
              <option value="flat">Flat Amount</option>
              <option value="percent">% of Rent</option>
            </select>
          </div>
          {feeType === 'flat' ? (
            <div className="fi">
              <label>Flat Fee ($)</label>
              <input type="number" min={0} value={feeAmt} onChange={e => setFeeAmt(parseFloat(e.target.value)||0)}/>
            </div>
          ) : (
            <div className="fi">
              <label>Percentage (%)</label>
              <input type="number" min={0} max={100} step={0.5} value={feePct} onChange={e => setFeePct(parseFloat(e.target.value)||0)}/>
            </div>
          )}
        </div>
      </div>

      <div className="sg3">
        <div className="st">
          <div className="stb" style={{ background:'linear-gradient(90deg,#f85149,#fc7b7b)' }}/>
          <small>Late Tenants</small>
          <div className="stv">{lateTenants.length}</div>
          <div className="sts">of {tenants.length} total</div>
        </div>
        <div className="st">
          <div className="stb" style={{ background:'linear-gradient(90deg,#f97316,#fb923c)' }}/>
          <small>Outstanding Balance</small>
          <div className="stv">{$(totalBalance)}</div>
          <div className="sts">unpaid rent</div>
        </div>
        <div className="st">
          <div className="stb" style={{ background:'linear-gradient(90deg,#d29922,#e8b84b)' }}/>
          <small>Late Fees Due</small>
          <div className="stv">{$(totalFees)}</div>
          <div className="sts">{feeType === 'flat' ? `$${feeAmt} flat` : `${feePct}% of rent`}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-hd">
          <h3>Late Tenants</h3>
          {lateTenants.length > 0 && addLateFee && (
            <button className="btn btn-d" style={{ padding:'6px 14px', fontSize:12 }} onClick={chargeAll}>
              <Ic n="alert" s={13}/> Charge All
            </button>
          )}
        </div>

        {lateTenants.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 20px', color:'#484f58' }}>
            <Ic n="check" s={36} c="#22c55e"/>
            <div style={{ marginTop:12, fontSize:13, color:'#22c55e' }}>All tenants are current — no late fees apply</div>
          </div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr><th>Tenant</th><th>Unit</th><th>Monthly Rent</th><th>Days Late</th><th>Balance Due</th><th>Late Fee</th><th>Status</th></tr>
              </thead>
              <tbody>
                {lateTenants.map(({ tenant: t, rent, daysLate, fee, balance }) => {
                  const charged = chargedIds.has(t.id);
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="tc">
                          <div className="av" style={{ width:30, height:30, fontSize:10 }}>
                            {(t.full_name||t.name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <span style={{ fontWeight:600, color:'#fff' }}>{t.full_name||t.name}</span>
                        </div>
                      </td>
                      <td style={{ color:'#8b949e' }}>Unit {t.unit||t.u||'—'}</td>
                      <td style={{ color:'#f5f0e8' }}>{$(rent)}</td>
                      <td><span className="b br">{daysLate > 0 ? `${daysLate}d late` : 'Grace period'}</span></td>
                      <td style={{ color:'#f85149', fontWeight:700 }}>{$(balance)}</td>
                      <td style={{ color:'#d29922', fontWeight:700 }}>{$(fee)}</td>
                      <td>
                        {charged
                          ? <span className="b bg">Charged</span>
                          : addLateFee
                            ? <button className="btn btn-gh" style={{ padding:'4px 10px', fontSize:11 }}
                                disabled={saving === t.id}
                                onClick={() => chargeFee(t, fee)}>
                                {saving === t.id ? '...' : `Charge ${$(fee)}`}
                              </button>
                            : <span className="b b">Preview</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {lateFees.length > 0 && (
          <div style={{ marginTop:20, paddingTop:16, borderTop:'1px solid #21262d' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#8b949e', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Charge History</div>
            {lateFees.slice(0, 10).map(f => (
              <div key={f.id} className="row">
                <div>
                  <div style={{ fontSize:13, fontWeight:500 }}>{f.tenant_name || '—'}</div>
                  <div style={{ fontSize:11, color:'#484f58' }}>{fd(f.charged_at)} · {f.fee_type}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontWeight:700, color:'#d29922' }}>{$(f.amount)}</div>
                  <span className={`b ${f.status === 'paid' ? 'bg' : f.status === 'waived' ? 'b' : 'by'}`} style={{ fontSize:10 }}>{f.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
